import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Caas from '../../../../../api/caas';
import useApiQuery from '../../../../../hooks/useApiQuery';

import useClusterUpgradeStatus from './useClusterUpgradeStatus';

import type { APIHookArg } from '@/types';
import type { ClusterUpgradeStatusItem, UpgradeEligibilityResponse } from '@/types/caasTypes';

interface UseClusterUpgradeReturn {
  statusItem: ClusterUpgradeStatusItem | undefined;
  statusError: Error | null;
  upgradeEligibility: UpgradeEligibilityResponse | null;
  isValidationLoading: boolean;
  validationError: Error | null;
  isLive: boolean;
  connectionLost: boolean;
  upgradeRequested: boolean;
  selectedVersion: string;
  setSelectedVersion: (v: string) => void;
  isUpgradeInProgress: boolean;
  isUpgradeFinished: boolean;
  isAwaitingPostCheck: boolean;
  canRunValidationCheck: boolean;
  isEligible: boolean;
  canUpgrade: boolean;
  isFullyComplete: boolean;
  newUpgradeStarted: boolean;
  startNewUpgrade: () => void;
  handleUpgrade: () => Promise<void>;
  refetchStatus: () => void;
  revalidate: () => void;
}

export default function useClusterUpgrade(clusterName: string): UseClusterUpgradeReturn {
  const intl = useIntl();
  const [upgradeRequested, setUpgradeRequested] = useState(false);
  const upgradeRequestedRef = useRef(false);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [newUpgradeStarted, setNewUpgradeStarted] = useState(false);

  const {
    statusItem,
    latestStatus,
    statusError,
    isLive,
    connectionLost,
    isUpgradeInProgress,
    isUpgradeFinished,
    isPostCheckResolved,
    isAwaitingPostCheck,
    canRunValidationCheck,
    refetchStatus,
  } = useClusterUpgradeStatus(clusterName, { upgradeRequested });

  const {
    makeApiCall: fetchUpgradeEligibility,
    data: fetchedUpgradeEligibility = null,
    isLoading: isValidationLoading,
    error: validationError,
    resetError: resetEligibilityError,
  } = useApiQuery<UpgradeEligibilityResponse>(
    Caas.fetchUpgradeEligibility as unknown as (
      args: unknown
    ) => Promise<UpgradeEligibilityResponse>,
    { disableLoader: true }
  );

  // `resetError` (unlike the old local hook's `reset`) only clears the error,
  // not the fetched data - mirror the old "drop stale eligibility" behavior
  // locally instead, since useApiQuery has no data-reset of its own.
  const [eligibilityCleared, setEligibilityCleared] = useState(false);
  const upgradeEligibility = eligibilityCleared ? null : fetchedUpgradeEligibility;

  useEffect(() => {
    if (fetchedUpgradeEligibility) {
      setEligibilityCleared(false);
    }
  }, [fetchedUpgradeEligibility]);

  const { makeApiCall: upgradeCluster } = useApiQuery(
    Caas.upgradeCluster as unknown as APIHookArg,
    {
      successMessage: intl.formatMessage({ id: 'caasupgradeSuccess' }),
      disableLoader: true,
    }
  );

  const isFullyComplete = isUpgradeFinished && isPostCheckResolved;

  useEffect(() => {
    if (!latestStatus || isUpgradeInProgress) return;

    if (upgradeRequestedRef.current && isFullyComplete) {
      setUpgradeRequested(false);
      upgradeRequestedRef.current = false;
    }

    if (upgradeRequestedRef.current && latestStatus.message?.includes('No upgrades found for')) {
      setUpgradeRequested(false);
      upgradeRequestedRef.current = false;
    }

    if (!canRunValidationCheck) return;

    // After a completed upgrade, don't auto-revalidate — hold on the settled
    // "complete" view until the user explicitly starts a new upgrade.
    if (isFullyComplete && !newUpgradeStarted) return;

    fetchUpgradeEligibility(clusterName);
  }, [
    clusterName,
    canRunValidationCheck,
    fetchUpgradeEligibility,
    isUpgradeInProgress,
    isFullyComplete,
    newUpgradeStarted,
    latestStatus,
  ]);

  const isEligible = upgradeEligibility?.eligibleForUpgrade === true;
  const canUpgrade =
    isEligible &&
    !!selectedVersion &&
    !isUpgradeInProgress &&
    !isAwaitingPostCheck &&
    !upgradeRequested &&
    !isValidationLoading;

  const handleUpgrade = async (): Promise<void> => {
    if (!selectedVersion) return;
    const response = await upgradeCluster(clusterName, selectedVersion).catch(() => null);
    if (!response) return;
    upgradeRequestedRef.current = true;
    setUpgradeRequested(true);
    setSelectedVersion('');
    setNewUpgradeStarted(false);
  };

  const startNewUpgrade = (): void => {
    // Drop the completed upgrade's eligibility so the new upgrade can't be
    // submitted against stale validation before the fresh check completes.
    resetEligibilityError();
    setEligibilityCleared(true);
    setSelectedVersion('');
    setNewUpgradeStarted(true);
  };

  return {
    statusItem,
    statusError,
    upgradeEligibility,
    isValidationLoading,
    validationError,
    isLive,
    connectionLost,
    upgradeRequested,
    selectedVersion,
    setSelectedVersion,
    isUpgradeInProgress,
    isUpgradeFinished,
    isAwaitingPostCheck,
    canRunValidationCheck,
    isEligible,
    canUpgrade,
    isFullyComplete,
    newUpgradeStarted,
    startNewUpgrade,
    handleUpgrade,
    refetchStatus,
    revalidate: () => {
      if (!canRunValidationCheck) return;
      fetchUpgradeEligibility(clusterName);
    },
  };
}
